<template>
  <ProductAnchorNav
    :should-show-description="!!(description)"
    :should-show-faq="!!(faq.length)"
    :should-show-related-accessories="!!(shouldShowRelatedAccessories)"
    :should-show-parents-for-accessory="!!(shouldShowParentsForAccessory)"
    :should-show-about-brand="!!(aboutBrand)"
    :should-show-sibling-categories="!!siblingCategories.length"
  >
    <ProductInformation
      v-if="product"
      :product-description="description"
      :product="product"
      :attributes-localized="attributesLocalized"
      :faq="faq"
      :about-brand="aboutBrand"
      :related-accessories="relatedAccessories"
      :related-accessories-loading="relatedAccessoriesLoading"
      :parents-for-accessory="parentsForAccessory"
      :parents-for-accessory-loading="parentsForAccessoryLoading"
      :should-show-related-accessories="!!(shouldShowRelatedAccessories)"
      :should-show-parents-for-accessory="!!(shouldShowParentsForAccessory)"
      :sibling-categories="siblingCategories"
      :alternative-products-data="alternativeProductsData"
      :similar-products="similarProducts"
      :similar-products-loading="similarProductsLoading"
    />
    <ProductInformationSkeleton v-else />
    <AnchorNavigationSectionWrapper
      v-if="product"
      :id="PDP_NAV_ANCHORS.RATING_REVIEWS"
    >
      <RatingsReviews
        :reviews="reviews"
        :reviews-count="reviewsCount"
        :rating="productRating"
        :sku="product.sku"
        :product="product"
      />
    </AnchorNavigationSectionWrapper>
    <RatingsReviewsSkeleton v-else />
    <!-- <AnchorNavigationSectionWrapper
      v-if="product"
    >
    TODO Nuxt 3 trustbox
      <TrustPilotCarousel />
    </AnchorNavigationSectionWrapper> -->
    <AnchorNavigationSectionWrapper
      v-if="product"
      :id="PDP_NAV_ANCHORS.FAQ"
    >
      <FaqSection :faq="faq" />
    </AnchorNavigationSectionWrapper>
  </ProductAnchorNav>
</template>

<script lang="ts">
import type { ProductVariant } from '~~/types/commercetools';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { PDP_NAV_ANCHORS } from '~~/constants/pdpNavAnchors';
import { productGetters } from '~/utils/ct';

import { isSecondHandProduct } from '~~/helpers/product/getProductInfo/getProductInfo';
import ProductInformationSkeleton from '~/components/molecules/PdpSkeletons/ProductInformationSkeleton.vue';
import RatingsReviewsSkeleton from '~/components/molecules/PdpSkeletons/RatingsReviewsSkeleton.vue';
import ProductAnchorNav from '~/components/molecules/Product/ProductAnchorNav.vue';
import AnchorNavigationSectionWrapper from '~/components/molecules/Product/AnchorNavigationSectionWrapper.vue';
import type { ProductReview } from '~~/types/product/attribute/ProductReview';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import { getBrandInfo } from '~~/helpers/brand';
import getProductDescription from '~~/helpers/product/getProductDescription/getProductDescription';
import TrustPilotCarousel from '~/components/molecules/TrustPilot/TrustPilotCarousel.vue';
import FaqSection from '~/components/organisms/Product/FaqSection.vue';
import type { AlternativeProductData } from '~~/types/product/AlternativeProducts';

export default defineComponent({
  name: 'ProductAnchorSections',
  components: {
    RatingsReviews: () => import(/* webpackChunkName: "RatingsReviews" */
      '~/components/molecules/Product/RatingsReviews/RatingsReviews.vue'),
    ProductInformation: () => import(/* webpackChunkName: "ProductInformation" */
      '~/components/organisms/Product/ProductInformation.vue'),
    ProductInformationSkeleton,
    RatingsReviewsSkeleton,
    ProductAnchorNav,
    AnchorNavigationSectionWrapper,
    TrustPilotCarousel,
    FaqSection,
  },
  props: {
    product: {
      type: Object as PropType<ProductVariantExtended>,
      default: () => {},
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      default: () => {},
    },
    reviews: {
      type: Array as PropType<Array<ProductReview>>,
      required: true,
    },
    relatedAccessories: {
      type: Array as PropType<ProductVariant[]>,
      default: () => [],
    },
    relatedAccessoriesLoading: {
      type: Boolean,
      required: true,
    },
    parentsForAccessory: {
      type: Array as PropType<ProductVariant[]>,
      default: () => [],
    },
    parentsForAccessoryLoading: {
      type: Boolean,
      required: true,
    },
    shouldShowRelatedAccessories: {
      type: Boolean,
      required: true,
    },
    alternativeProductsData: {
      type: Array as PropType<AlternativeProductData[]>,
      default: () => [],
    },
    similarProducts: {
      type: Array as PropType<ProductVariantExtended[]>,
      default: () => [],
    },
    similarProductsLoading: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {

    const { product } = toRefs(props);

    const { languageAndCountry } = useLocalization();

    const faq = computed(() => props.attributesLocalized?.faq || []);
    const aboutBrand = computed(() => {

      const brandInfo = getBrandInfo(props.attributesLocalized?.brand);

      if (!brandInfo?.logo && !brandInfo?.name) {

        return;

      }

      return brandInfo;

    });
    const productId = computed(() => productGetters.getId(product.value));
    const description = computed(
      () => getProductDescription(product.value, languageAndCountry.value),
    );

    const shouldShowParentsForAccessory = computed(() =>
      props.parentsForAccessory &&
      props.parentsForAccessory.length &&
      !isSecondHandProduct(product.value),
    );

    const { siblingCategories, load: loadSiblingCategories } = useSiblingCategories();

    const productSku = computed(() => product.value?.sku || null);

    onMounted(() => {

      if (product.value) {

        loadSiblingCategories(product.value);

      }

    });

    watch(
      productSku,
      () => {

        const newValue = product.value;

        if (newValue) {

          loadSiblingCategories(newValue);

        }

      },
    );

    return {
      description,
      faq,
      aboutBrand,
      reviewsCount: computed(() => productGetters.getTotalReviews(product.value)),
      productRating: computed(() => productGetters.getAverageRating(product.value)),
      shouldShowParentsForAccessory,
      PDP_NAV_ANCHORS,
      productId,
      siblingCategories,
    };

  },
});
</script>
